import { getFoodieProgress, earnBadge } from './foodieProgress.js';
import { launchConfetti } from './confetti.js';

// Badge thresholds (count of visited places)
const BADGE_THRESHOLDS = [
  { count: 1,   id: 'explorer',     label: 'Explorer' },
  { count: 10,  id: 'adventurer',   label: 'Adventurer' },
  { count: 25,  id: 'excursioneer', label: 'Excursioneer' },
  { count: 50,  id: 'voyager',      label: 'Voyager' },
  { count: 75,  id: 'connoisseur',  label: 'Connoisseur' },
  { count: 100, id: 'legend',       label: 'Legend' }
];

/**
 * Checks if user qualifies for new badges based on visited count
 * and awards them if not already earned.
 */
export async function checkAndAwardBadges(visitedCountOverride = null) {
  const progress = await getFoodieProgress();
  const visitedCount = visitedCountOverride ?? (progress.visited?.length || 0);
  const currentBadges = new Set(progress.badges || []);
  let newBadges = [];

  for (const badge of BADGE_THRESHOLDS) {
    if (visitedCount >= badge.count && !currentBadges.has(badge.id)) {
      await earnBadge(badge.id);  // ✅ save new badge
      newBadges.push(badge);
    }
  }

  if (newBadges.length) {
    showBadgeUnlockAnimation(newBadges);
  }
}

/**
 * Badge unlock popup animation
 */
function showBadgeUnlockAnimation(newBadges) {
  newBadges.forEach(badge => {
    const modal = document.createElement('div');
    modal.className = 'unlock-popup';
    
    // Get correct profile URL based on domain with anchor to badges section
    const basePath = window.location.hostname.includes('fogg.app') ? '/' : '/fogg/';
    const profileUrl = `${basePath}profile#badges`;
    
    modal.innerHTML = `
      <div class="unlock-popup-inner">
        <a href="${profileUrl}"><img src="/images/badges/${badge.id}.png" alt="${badge.label}" class="unlock-icon" /></a>
        <div class="unlock-text">
          <strong>${badge.label}</strong><br>
          Badge Unlocked!
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    requestAnimationFrame(() => modal.classList.add('visible'));

    // Auto-remove after animation
    setTimeout(() => {
      modal.classList.remove('visible');
      setTimeout(() => modal.remove(), 500);
    }, 4000);
  });

  // launchConfetti();
}

/**
 * Render "My Badges" gallery
 */
export async function renderBadgesGallery() {
  const container = document.getElementById('badges-gallery');
  if (!container) return;

  const { badges = [] } = await getFoodieProgress();

  container.innerHTML = '';
  
  // Remove any existing no-badges message
  const existingMessage = container.nextElementSibling;
  if (existingMessage && existingMessage.classList.contains('no-badges')) {
    existingMessage.remove();
  }

  // If no badges, show message before rendering placeholders
  if (!badges.length) {
    container.insertAdjacentHTML('beforebegin', `<p class="no-badges">No badges yet! Earn badges by visiting FoGG eateries and marking them visited.</p>`);
  }

  // Create a set of earned badge IDs for quick lookup
  const earnedSet = new Set(badges);
  
  // Render all badges in order from BADGE_THRESHOLDS
  BADGE_THRESHOLDS.forEach(badgeConfig => {
    const isEarned = earnedSet.has(badgeConfig.id);
    const el = document.createElement('div');
    el.className = 'badge-item';
    
    // Add tooltip attributes
    el.setAttribute('data-bs-toggle', 'tooltip');
    el.setAttribute('data-bs-placement', 'top');
    el.style.cursor = 'pointer';
    
    if (isEarned) {
      el.setAttribute('data-bs-title', `Unlocked!`);
      el.innerHTML = `
        <img src="/images/badges/${badgeConfig.id}.png" alt="${badgeConfig.label}" />
        <span class="badge-label">${badgeConfig.label}</span>
      `;
    } else {
      el.classList.add('locked');
      const eateryText = badgeConfig.count === 1 ? 'eatery' : 'eateries';
      el.setAttribute('data-bs-title', `Visit ${badgeConfig.count} ${eateryText} to unlock`);
      el.innerHTML = `
        <img src="/images/badges/${badgeConfig.id}.png" alt="Locked ${badgeConfig.label}" />
        <span class="badge-label locked-label">${badgeConfig.label}</span>
      `;
    }
    
    container.appendChild(el);
  });
  
  // Initialize Bootstrap tooltips
  initializeTooltips();
}

/**
 * Initialize custom tooltips (simple implementation)
 */
function initializeTooltips() {
  const badgeItems = document.querySelectorAll('.badge-item[data-bs-toggle="tooltip"]');
  
  badgeItems.forEach(item => {
    item.addEventListener('mouseenter', (e) => {
      const title = item.getAttribute('data-bs-title');
      if (!title) return;
      
      // Create tooltip element
      const tooltip = document.createElement('div');
      tooltip.className = 'custom-tooltip';
      tooltip.innerHTML = title; // Use innerHTML to support <br> tags
      document.body.appendChild(tooltip);
      
      // Position tooltip
      const rect = item.getBoundingClientRect();
      let left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
      
      // Keep tooltip on screen
      const padding = 8;
      if (left < padding) {
        left = padding;
      } else if (left + tooltip.offsetWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltip.offsetWidth - padding;
      }
      
      tooltip.style.left = left + 'px';
      tooltip.style.top = rect.top - tooltip.offsetHeight - 8 + 'px';
      
      // Show tooltip
      requestAnimationFrame(() => tooltip.classList.add('show'));
      
      // Store reference for removal
      item._tooltip = tooltip;
    });
    
    item.addEventListener('mouseleave', () => {
      if (item._tooltip) {
        item._tooltip.classList.remove('show');
        setTimeout(() => {
          if (item._tooltip) {
            item._tooltip.remove();
            item._tooltip = null;
          }
        }, 300); // Faster fade-out on mouseleave
      }
    });
    
    // Also dismiss tooltip on touch/click for mobile
    item.addEventListener('click', () => {
      if (item._tooltip) {
        item._tooltip.classList.remove('show');
        setTimeout(() => {
          if (item._tooltip) {
            item._tooltip.remove();
            item._tooltip = null;
          }
        }, 100);
      }
    });
  });
}
